import React, { useState, useEffect, useMemo } from 'react'
import {
  Search,
  Mail as MailIcon,
  Edit,
  UserX,
  Key,
  Lock,
  Unlock,
  X,
  Loader2,
  ChevronDown
} from 'lucide-react'
import { Button, Badge } from '../ui'
import { userService, User, UserListParams } from '../../services/userService'
import { authService, Role } from '../../services/authService'
import { useAuthStore } from '../../stores/authStore'
import {
  getFilteredRolesForDisplay,
  formatRoleName
} from '../../utils/roleHelpers'
import InviteUserModal from './InviteUserModal'
import UserDrawer from './UserDrawer'

type SortField = 'organizationName' | 'firstName' | 'email' | 'lastLoginAt'
type SortDir = 'asc' | 'desc'

const UserList: React.FC = () => {
  const { user: currentUser } = useAuthStore()
  const [users, setUsers] = useState<User[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)

  // Filters
  const [filterRole, setFilterRole] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [filterOrganization] = useState<string>('')

  // Sorting
  const [sortField, setSortField] = useState<SortField>('organizationName')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalUsers, setTotalUsers] = useState(0)
  const limit = 20

  // Filter roles based on current user's permissions
  const filteredRoles = useMemo(
    () => getFilteredRolesForDisplay(roles, currentUser),
    [roles, currentUser]
  )

  const loadUsers = async (params: UserListParams = {}) => {
    try {
      setLoading(true)
      setError(null)

      const response = await userService.getUsers({
        page: currentPage,
        limit,
        search: debouncedSearchTerm || undefined,
        roleId: filterRole ? parseInt(filterRole) : undefined,
        statusId: filterStatus ? parseInt(filterStatus) : undefined,
        organizationId: filterOrganization || undefined,
        excludeSupport: true,
        ...params
      })

      if (response.success) {
        setUsers(response.data.users)
        setTotalUsers(response.data.pagination.total)
        setTotalPages(Math.ceil(response.data.pagination.total / limit))
      } else {
        throw new Error('Failed to load users')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
    } finally {
      setLoading(false)
      setIsSearching(false)
    }
  }

  const loadRoles = async () => {
    try {
      const response = await authService.getRoles()
      if (response.success) {
        setRoles(response.data)
      }
    } catch (err) {
      console.error('Failed to load roles:', err)
    }
  }

  useEffect(() => {
    loadRoles()
  }, [])

  // Debounce search term with 400ms delay
  useEffect(() => {
    setIsSearching(true)
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 400)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset page to 1 when filters or search changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1)
    }
  }, [debouncedSearchTerm, filterRole, filterStatus, filterOrganization])

  // Load users when page or filters change
  useEffect(() => {
    loadUsers()
  }, [currentPage, filterRole, filterStatus, filterOrganization, debouncedSearchTerm])

  // Client-side sort of current page
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      let aVal: string
      let bVal: string

      if (sortField === 'firstName') {
        aVal = `${a.firstName} ${a.lastName}`.toLowerCase()
        bVal = `${b.firstName} ${b.lastName}`.toLowerCase()
      } else if (sortField === 'lastLoginAt') {
        aVal = a.lastLoginAt ?? ''
        bVal = b.lastLoginAt ?? ''
      } else {
        aVal = (a[sortField] ?? '').toLowerCase()
        bVal = (b[sortField] ?? '').toLowerCase()
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }, [users, sortField, sortDir])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('asc')
    }
  }

  const handleEditUser = (user: User) => {
    setSelectedUser(user)
    setIsDrawerOpen(true)
  }

  const handleInviteUser = () => {
    setIsInviteModalOpen(true)
  }

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false)
    setSelectedUser(null)
  }

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false)
  }

  const handleUserSaved = () => {
    loadUsers()
    handleCloseDrawer()
  }

  const handleInviteSent = () => {
    loadUsers()
    handleCloseInviteModal()
  }

  const handleClearSearch = () => {
    setSearchTerm('')
  }

  const handleUserAction = async (action: string, user: User) => {
    if (!currentUser?.organizationId) {
      setError('Organization ID not found')
      return
    }

    try {
      setLoading(true)
      let response

      switch (action) {
        case 'reset-password':
          response = await userService.resetUserPassword(user.id)
          break
        case 'disable':
          response = await userService.disableUser(currentUser.organizationId, user.id)
          break
        case 'enable':
          response = await userService.enableUser(currentUser.organizationId, user.id)
          break
        default:
          return
      }

      if (response.success) {
        loadUsers()
      } else {
        setError(response.error?.message || 'Action failed')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed')
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadgeVariant = (statusName: string) => {
    switch (statusName.toLowerCase()) {
      case 'active':
        return 'success'
      case 'pending':
        return 'warning'
      case 'locked':
      case 'inactive':
        return 'error'
      default:
        return 'default'
    }
  }

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString()
  }

  const SortableHeader: React.FC<{ field: SortField; label: string }> = ({ field, label }) => (
    <button
      onClick={() => handleSort(field)}
      className='flex items-center space-x-1 text-[11px] font-semibold text-gray-700 uppercase tracking-wider hover:text-gray-900 transition-colors'
    >
      <span>{label}</span>
      <ChevronDown className={`h-3 w-3 transition-transform ${
        sortField === field ? (sortDir === 'asc' ? 'rotate-180' : 'rotate-0') : 'opacity-30'
      }`} />
    </button>
  )

  if (loading && users.length === 0) {
    return (
      <div className='space-y-6'>
        <div className='flex items-center justify-between'>
          <h2 className='text-xl font-semibold text-gray-900'>User Management</h2>
        </div>
        <div className='flex items-center justify-center py-12'>
          <div className='text-gray-500'>Loading users...</div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className='space-y-6'>
        {/* Header */}
        <div className='flex items-center justify-between'>
          <div>
            <h2 className='text-sm font-semibold text-gray-900'>User Management</h2>
            <p className='text-xs text-gray-600'>Manage user accounts and permissions</p>
          </div>
          <Button size="xs" onClick={handleInviteUser}>
            <MailIcon className='h-3.5 w-3.5 mr-2' />
            Invite User
          </Button>
        </div>

        {/* Filters */}
        <div className='flex flex-wrap items-center gap-4'>
          <div className='flex-1 min-w-64'>
            <div className='relative'>
              <Search className='h-3.5 w-3.5 absolute left-3 top-2.5 text-gray-400' />
              <input
                type='text'
                placeholder='Search users...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full pl-9 pr-20 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent text-xs'
              />
              <div className='absolute right-3 top-2 flex items-center gap-2'>
                {isSearching && searchTerm && (
                  <Loader2 className='h-3.5 w-3.5 text-gray-400 animate-spin' />
                )}
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className='p-0.5 hover:bg-gray-200 rounded transition-colors'
                    aria-label='Clear search'
                  >
                    <X className='h-3.5 w-3.5 text-gray-500 hover:text-gray-700' />
                  </button>
                )}
              </div>
            </div>
          </div>

          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className='px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent text-xs'
          >
            <option value=''>All Roles</option>
            {filteredRoles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.displayName}
              </option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className='px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent text-xs'
          >
            <option value=''>All Status</option>
            <option value='1'>Active</option>
            <option value='2'>Pending</option>
            <option value='3'>Locked</option>
          </select>
        </div>

        {/* Error State */}
        {error && (
          <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-xs'>
            {error}
          </div>
        )}

        {/* Users Table */}
        <div className='bg-white border border-gray-200 rounded-lg overflow-hidden'>
          {/* Table Header */}
          <div className='grid grid-cols-12 gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200'>
            <div className='col-span-2'>
              <SortableHeader field='organizationName' label='Organization' />
            </div>
            <div className='col-span-2'>
              <SortableHeader field='firstName' label='Name' />
            </div>
            <div className='col-span-2'>
              <SortableHeader field='email' label='Email' />
            </div>
            <div className='col-span-2'>
              <span className='text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>Role</span>
            </div>
            <div className='col-span-1'>
              <span className='text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>Status</span>
            </div>
            <div className='col-span-1'>
              <SortableHeader field='lastLoginAt' label='Last Login' />
            </div>
            <div className='col-span-2 text-right'>
              <span className='text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>Actions</span>
            </div>
          </div>

          {/* Table Rows */}
          <div className='divide-y divide-gray-100'>
            {sortedUsers.map((user) => (
              <div
                key={user.id}
                className='grid grid-cols-12 gap-2 px-4 py-2 hover:bg-gray-50 transition-colors'
              >
                <div className='col-span-2 flex items-center'>
                  <p className='text-[11px] text-gray-700 truncate'>{user.organizationName}</p>
                </div>
                <div className='col-span-2 flex items-center'>
                  <p className='text-[11px] text-gray-900 font-medium truncate'>
                    {user.firstName} {user.lastName}
                  </p>
                </div>
                <div className='col-span-2 flex items-center'>
                  <p className='text-[11px] text-gray-600 truncate'>{user.email}</p>
                </div>
                <div className='col-span-2 flex items-center'>
                  <p className='text-[11px] text-gray-900 capitalize'>
                    {formatRoleName(user.roleName)}
                  </p>
                </div>
                <div className='col-span-1 flex items-center'>
                  <Badge variant={getStatusBadgeVariant(user.statusName)}>
                    {user.statusName}
                  </Badge>
                </div>
                <div className='col-span-1 flex items-center'>
                  <p className='text-[11px] text-gray-600'>{formatDate(user.lastLoginAt)}</p>
                </div>
                <div className='col-span-2 flex items-center justify-end'>
                  <div className='relative group'>
                    <Button
                      variant='ghost'
                      size='xs'
                      onClick={() => handleUserAction('reset-password', user)}
                      disabled={user.statusName.toLowerCase() === 'inactive'}
                    >
                      <Key className='h-3.5 w-3.5' />
                    </Button>
                    <div className='absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50'>
                      Reset Password
                    </div>
                  </div>
                  {user.statusName.toLowerCase() === 'active' ? (
                    <div className='relative group'>
                      <Button
                        variant='ghost'
                        size='xs'
                        onClick={() => handleUserAction('disable', user)}
                      >
                        <Lock className='h-3.5 w-3.5' />
                      </Button>
                      <div className='absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50'>
                        Disable User
                      </div>
                    </div>
                  ) : (
                    <div className='relative group'>
                      <Button
                        variant='ghost'
                        size='xs'
                        onClick={() => handleUserAction('enable', user)}
                      >
                        <Unlock className='h-3.5 w-3.5' />
                      </Button>
                      <div className='absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50'>
                        Enable User
                      </div>
                    </div>
                  )}
                  <div className='relative group'>
                    <Button
                      variant='ghost'
                      size='xs'
                      onClick={() => handleEditUser(user)}
                      disabled={user.statusName.toLowerCase() === 'inactive'}
                    >
                      <Edit className='h-3.5 w-3.5' />
                    </Button>
                    <div className='absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50'>
                      Edit User
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className='flex items-center justify-between'>
            <p className='text-xs text-gray-700'>
              Showing {(currentPage - 1) * limit + 1} to{' '}
              {Math.min(currentPage * limit, totalUsers)} of {totalUsers} users
            </p>
            <div className='flex items-center space-x-2'>
              <Button
                variant='outline'
                size='xs'
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className='text-xs text-gray-600'>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant='outline'
                size='xs'
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && users.length === 0 && (
          <div className='text-center py-12'>
            <UserX className='h-10 w-10 text-gray-400 mx-auto mb-3' />
            <h3 className='text-sm font-medium text-gray-900 mb-2'>No users found</h3>
            <p className='text-xs text-gray-600 mb-4'>
              {searchTerm || filterRole || filterStatus
                ? 'Try adjusting your search criteria.'
                : 'Get started by creating a new user.'}
            </p>
            {!searchTerm && !filterRole && !filterStatus && (
              <Button size="xs" onClick={handleInviteUser}>
                <MailIcon className='h-3.5 w-3.5 mr-2' />
                Invite User
              </Button>
            )}
          </div>
        )}
      </div>

      {/* User Drawer - Edit Only */}
      <UserDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        user={selectedUser}
        onSaved={handleUserSaved}
      />

      {/* Invite User Modal */}
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={handleCloseInviteModal}
        onInviteSent={handleInviteSent}
      />
    </div>
  )
}

export default UserList
